import React, { useState, useEffect } from 'react';
import {
  Button,
  Alert,
  Paper,
  Grid,
  Typography,
  Divider,
  Box,
  CircularProgress,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { CardElement, ElementsConsumer } from '@stripe/react-stripe-js';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';

import {
  clearErrorMessage,
  setErrorMessage,
} from '../../../actions/errorMessage';
import { fetchUser } from '../../../actions/user';
import history from '../../../history';
import api from '../../../apis/api';
import getMessageFromApiError from '../../../util/getMessageFromApiError';

const PaymentMethodCardSection = ({
  stripe,
  elements,
  billingDetails,
  submitting: parentSubmitting,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const apiErrorMessage = useSelector((state) => state.errorMessage);
  const [stripeErrorMessage, setStripeErrorMessage] = useState('');
  const [complete, setComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(clearErrorMessage());
    };
  }, [dispatch]);

  const createOptions = () => ({
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        fontFamily: 'Open Sans, sans-serif',
        letterSpacing: '0.025em',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#c23d4b',
      },
    },
  });

  const handleChange = ({ complete, error }) => {
    setComplete(complete);
    setStripeErrorMessage(error ? error.message : '');
    dispatch(clearErrorMessage());
  };

  const cardSubmit = async (id) => {
    try {
      const res = await api.post('/customer/payment/method', { id });
      await stripe.confirmCardSetup(res.data.client_secret, {
        payment_method: res.data.payment_method,
      });
      history.push('/account/billing');
      dispatch(fetchUser());
    } catch (error) {
      const message = getMessageFromApiError(error);
      dispatch(setErrorMessage(message));
      setSubmitting(false);
    }
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    setSubmitting(true);

    if (!stripe) {
      console.log("Stripe.js hasn't loaded yet.");
      return;
    }

    const cardElement = elements.getElement('card');
    const paymentMethod = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name: billingDetails.name,
        address: {
          line1: billingDetails.address_line1,
          line2: billingDetails.address_line2,
          city: billingDetails.address_city,
          postal_code: billingDetails.address_postal_code,
          state: billingDetails.address_state,
          country: billingDetails.address_country,
        },
        phone: billingDetails.phone,
        email: billingDetails.email,
      },
    });

    if (paymentMethod.paymentMethod) {
      await cardSubmit(paymentMethod.paymentMethod.id);
    }
    if (paymentMethod.error) {
      setStripeErrorMessage(paymentMethod.error.message);
      setSubmitting(false);
    }
  };

  const renderErrorMessages = () => {
    if (stripeErrorMessage) {
      return <Alert severity='error'>{stripeErrorMessage}</Alert>;
    }
    if (apiErrorMessage) {
      return (
        <Alert severity='error'>
          <Typography variant='subtitle2'>Add Payment Method Failed</Typography>
          {apiErrorMessage}
        </Alert>
      );
    }
    return null;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant='h5' gutterBottom>
        Credit Card Details
      </Typography>
      <Box component='form' onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CardElement onChange={handleChange} {...createOptions()} />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant='outlined'
                onClick={onCancel}
                startIcon={<CloseIcon />}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                color='brand'
                type='submit'
                disabled={
                  Boolean(apiErrorMessage) ||
                  !complete ||
                  Boolean(stripeErrorMessage) ||
                  submitting ||
                  parentSubmitting
                }
                startIcon={
                  submitting || parentSubmitting ? (
                    <CircularProgress size={20} color='inherit' />
                  ) : (
                    <CheckIcon />
                  )
                }
              >
                {submitting || parentSubmitting ? 'Adding...' : 'Add'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Divider sx={{ my: 2 }} />
      {renderErrorMessages()}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <img
          src='/powered_by_stripe.png'
          alt='stripe logo'
          style={{ height: '30px' }}
        />
      </Box>
    </Paper>
  );
};

const PaymentMethodCardSectionWithStripe = (props) => (
  <ElementsConsumer>
    {({ stripe, elements }) => (
      <PaymentMethodCardSection
        stripe={stripe}
        elements={elements}
        {...props}
      />
    )}
  </ElementsConsumer>
);

export default PaymentMethodCardSectionWithStripe;
